import type { SelectCorrectDb, SelectCorrectRuleStrategy } from "./types"

import {
  AKKUSATIV_DATIV_STRATEGY,
  DEFINITARTIKEL_STRATEGY,
  DETERMINER_ENDING_STRATEGY,
  KAUSALE_PRAEPOSITION_STRATEGY,
  LOKALE_PRAEPOSITION_STRATEGY,
  ORTSADVERB_STRATEGY,
  REDEWENDUNG_STRATEGY,
  RICHTUNGSADVERB_STRATEGY,
  RICHTUNGSPRAEPOSITION_STRATEGY,
  VERBPRAEFIX_STRATEGY,
  WECHSEL_ENDUNG_STRATEGY,
} from "./locate-matches"

export interface SelectCorrectRule {
  description: string
  id: string
  label: string
  /** Literal import thunk — each JSON gets its own lazily-loaded chunk. */
  load: ()=> Promise<SelectCorrectDb>
  strategy: SelectCorrectRuleStrategy
}

export const SELECT_CORRECT_RULES: SelectCorrectRule[] = [
  {
    description:
      "Der, den oder dem? Definitartikel im maskulinen Singular bestimmen.",
    id: "definitartikel-maskulin",
    label: "Definitartikel Maskulin",
    load: async ()=> {
      const module = await import(
        "../assets/german/select-correct/masculine-singular-prepositions.json"
      )
      return module.default as unknown as SelectCorrectDb
    },
    strategy: DEFINITARTIKEL_STRATEGY,
  },
  {
    description:
      "In, an, auf, zu oder um? Richtungspräposition im Akkusativ oder Dativ.",
    id: "richtungspraeposition",
    label: "Richtungspräposition",
    load: async ()=> {
      const module = await import(
        "../assets/german/select-correct/direction-prepositions.json"
      )
      return module.default as unknown as SelectCorrectDb
    },
    strategy: RICHTUNGSPRAEPOSITION_STRATEGY,
  },
  {
    description:
      "Welche Form passt? Artikel und Pronomen im ersten Satzteil beugen.",
    id: "akkusativ-dativ-erstteil",
    label: "Akkusativ + Dativ im ersten Satzteil",
    load: async ()=> {
      const module = await import(
        "../assets/german/select-correct/accusative-dative-first-part.json"
      )
      return module.default as unknown as SelectCorrectDb
    },
    strategy: AKKUSATIV_DATIV_STRATEGY,
  },
  {
    description:
      "Aus, bei oder nach? Die lokale Präposition hängt vom Sinn des Objekts ab.",
    id: "lokale-praeposition",
    label: "Lokale Präposition",
    load: async ()=> {
      const module = await import(
        "../assets/german/select-correct/local-prepositions.json"
      )
      return module.default as unknown as SelectCorrectDb
    },
    strategy: LOKALE_PRAEPOSITION_STRATEGY,
  },
  {
    description:
      "Kasus in Redewendungen: nur die Endung des Artikels oder Adjektivs.",
    id: "redewendung-kasus",
    label: "Kasus in Redewendungen",
    load: async ()=> {
      const module = await import(
        "../assets/german/select-correct/idiom-questions.json"
      )
      return module.default as unknown as SelectCorrectDb
    },
    strategy: REDEWENDUNG_STRATEGY,
  },
  {
    description:
      "Dativ oder Akkusativ? Die Endung des Artikels nach einer Wechselpräposition bestimmen.",
    id: "wechsel-endungen",
    label: "Wechselpräposition: Endung des Artikels",
    load: async ()=> {
      const module = await import(
        "../assets/german/select-correct/wechsel-endings.json"
      )
      return module.default as unknown as SelectCorrectDb
    },
    strategy: WECHSEL_ENDUNG_STRATEGY,
  },
  {
    description:
      "Hin oder her? Die Bewegung vom Sprecher weg oder auf ihn zu bestimmen.",
    id: "richtungsadverb-hin-her",
    label: "Richtungsadverb hin/her",
    load: async ()=> {
      const module = await import(
        "../assets/german/select-correct/hin-her-adverbs.json"
      )
      return module.default as unknown as SelectCorrectDb
    },
    strategy: RICHTUNGSADVERB_STRATEGY,
  },
  {
    description:
      "Oben oder unten? Das Ortsadverb wählen, das zum Satz passt.",
    id: "ortsadverb",
    label: "Ortsadverb",
    load: async ()=> {
      const module = await import(
        "../assets/german/select-correct/ort-adverbs.json"
      )
      return module.default as unknown as SelectCorrectDb
    },
    strategy: ORTSADVERB_STRATEGY,
  },
  {
    description:
      "Jed___, sämtlich___ oder diejenig___? Die Endung des Determinativs bestimmen.",
    id: "determinativendung",
    label: "Determinativendung",
    load: async ()=> {
      const module = await import(
        "../assets/german/select-correct/determiner-endings.json"
      )
      return module.default as unknown as SelectCorrectDb
    },
    strategy: DETERMINER_ENDING_STRATEGY,
  },
  {
    description: "Über, unter oder um? Das Präfix des Verbs bestimmen.",
    id: "verbpraefix",
    label: "Verbpräfix",
    load: async ()=> {
      const module = await import(
        "../assets/german/select-correct/verb-prefixes.json"
      )
      return module.default as unknown as SelectCorrectDb
    },
    strategy: VERBPRAEFIX_STRATEGY,
  },
  {
    description:
      "Vor oder aus? Die Kausalpräposition nach der Art des Kopfes bestimmen.",
    id: "kausalpraeposition",
    label: "Kausalpräposition vor/aus",
    load: async ()=> {
      const module = await import(
        "../assets/german/select-correct/kausal-prepositions.json"
      )
      return module.default as unknown as SelectCorrectDb
    },
    strategy: KAUSALE_PRAEPOSITION_STRATEGY,
  },
]

export const SELECT_CORRECT_RULE_OPTIONS: Record<string, string> =
  Object.fromEntries(SELECT_CORRECT_RULES.map(rule=> [rule.id, rule.label]))

export function findSelectCorrectRule(
  id: string,
): SelectCorrectRule | undefined {
  return SELECT_CORRECT_RULES.find(rule=> rule.id === id)
}
